import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUp, Check, Lock } from 'lucide-react';
import type { DashboardData, SkillPathway, ProgressData } from '@/services/dashboardService';
import { ProgressBarWithHandle } from './ProgressBarWithHandle';
import { useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
import './ProgressSection.css';

interface ProgressSectionProps {
  progress: ProgressData;
  selectedTree?: SkillPathway|null;
  selectedWay?: SkillPathway|null;
  hasData: boolean;
  onProgressUpdate?: (percentage: number) => void;
  loading?: boolean;
}

export const ProgressSection: React.FC<ProgressSectionProps> = ({
  progress,
  selectedTree,
  selectedWay,
  hasData,
  onProgressUpdate,
  loading = false
}) => {
  
  const [skillTree, setSkillTree] = useState<SkillPathway|null>(selectedWay as SkillPathway||null);
  const [totalMicroskills, setTotalMicroskills] = useState<number>(0);
  const [completedMicroskills, setCompletedMicroskills] = useState<number>(0);
  const [totalLearning, setTotalLearning] = useState<number>(0);
  const [completedLearning, setCompletedLearning] = useState<number>(0);
  const [totalAchievements, setTotalAchievements] = useState<number>(0);
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [microSkillsMap, setMicroSkillsMap] = useState<{ [unique_code: string]: any }>({});

  const navigate = useNavigate();
  

  const updateTree = (tree) => {
    let totalMS = 0;
    let completedMS = 0;
    let totalV = 0;
    let completedV = 0;
    let msMap: { [unique_code: string]: any } = {};

    if (tree && tree.versions) {
      console.log(tree);
      tree?.versions?.forEach(version => {
        version?.micro_skills?.forEach((microSkill, msid) => {
          totalMS++;
          if (microSkill.learning_status === 'completed') {
            completedMS++;
          }
          // if (microSkill.unique_code) {
            msMap[microSkill.unique_code || msid] = microSkill;
          // }
          // console.log('Micro Skills:', microSkill);
          microSkill?.videos?.forEach((video, vid) => {
            totalV++;
            if (video.learning_status === 'completed') {
              completedV++;
            }
          });
        });
      });
    }

    let totalAchieve = completedMS + completedV;
    let totalOverall = totalMS + totalV;
    let overallProg = totalOverall > 0 ? Math.round((totalAchieve / totalOverall) * 100) : 0;

    setMicroSkillsMap(msMap);
    setTotalMicroskills(totalMS);
    setCompletedMicroskills(completedMS);
    setTotalLearning(totalV);
    setCompletedLearning(completedV);
    setTotalAchievements(totalAchieve);
    setOverallProgress(overallProg);

    // console.log('Micro Skills Map:', microSkillsMap);
  }

  useEffect(() => {
    // console.log('Skill Tree:', skillTree);
    // console.log('Skill Pathway:', selectedTree, selectedWay);
    const newTree = selectedWay as SkillPathway || selectedTree as SkillPathway || null;
    setSkillTree(newTree);
    updateTree(newTree);
  }, [selectedWay, selectedTree]);

  const microCount = Object.keys(microSkillsMap).length;
  const isMicroEmpty = microCount === 0;
  const ongoingIdx = !isMicroEmpty ? Object.values(microSkillsMap).findIndex((m: any) => m.learning_status !== 'completed') : 0;
  const cId = selectedTree?.core_category_id || selectedWay?.core_category_id;
  const sId = selectedTree?.skillstree_id || selectedWay?.skillstree_id;
  // const mId = selectedTree?.versions?.[0]?.micro_skills?.[0]?.id;

  const goToSkillstree = (
    categoryId: string|number|null|undefined = null,
    skillstreeId: string|number|null|undefined = null,
    microskillId: string|number|null|undefined = null
  ) => {
    let path: string = `/skillstree`;
    if (categoryId || categoryId !== 'undefined') {
      path = `${path}/${categoryId}`;
      if (skillstreeId) {
        path = `${path}/${skillstreeId}`;
        if (microskillId) {
          path = `${path}/${microskillId}`;
        }
      }
    }
    navigate(path);
  };

  return (
    <>
      <div className="bg-white/10 rounded-lg px-8 py-4">
        {loading ? (
          // Loading state for progress section
          <div className="animate-pulse">
            <div className="text-center mb-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="h-5 bg-white/70 rounded mx-2"></div>
                <div className="h-5 bg-white/70 rounded mx-2"></div>
                <div className="h-5 bg-white/70 rounded mx-2"></div>
              </div>
              <div className="h-6 bg-white/70 rounded mt-4 w-1/4 mx-auto"></div>
            </div>
            <div className="h-5 bg-white/70 rounded"></div>
          </div>
        ) : (
          <div className="text-center mb-3">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                {/* <div className="w-4 h-4 bg-white rounded"></div> */}
                <span>{completedMicroskills || 0}/{totalMicroskills || 0} Micro Skills</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                {/* <div className="w-4 h-4 bg-white rounded"></div> */}
                <span>{completedLearning || 0}/{totalLearning || 0}  Micro Learning</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                {/* <div className="w-4 h-4 bg-white rounded"></div> */}
                <span>{totalAchievements || 0} Achievements</span>
              </div>
            </div>
            <div className="text-lg font-normal mt-4">
              {hasData ? overallProgress : 0}% learned
            </div>
          </div>
        )}
        
        {!loading && (
          <ProgressBarWithHandle
            progress={overallProgress}
            onProgressChange={onProgressUpdate}
          />
        )}
      </div>
      
      <div>
        <div className="w-full flex text-center justify-center mt-3">
          <span className="flex items-center justify-center text-white text-base font-bold me-3">
            {loading ? (
              <div className="animate-pulse bg-white/70 rounded h-6 w-32"></div>
            ) : (
              'My Skillstree'
            )}
          </span>
          {!loading && (
            <button onClick={() => goToSkillstree(cId, sId)} className="flex items-center justify-center w-8 h-8 min-w-8 min-h-8 rounded-full bg-black shadow-2xl shadow-cyan-500 border-2 border-cyan-500 aspect-square">
              <ArrowUp size={18} className="text-white rotate-45" />
            </button>
          )}
        </div>

        {loading ? (
          // Loading skeleton for microskills
          <div className="mt-4 flex justify-center space-x-4 overflow-x-auto pt-7 pb-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="flex items-center animate-pulse">
                <div className="w-24 h-24 bg-white/70 rounded-full flex-shrink-0"></div>
                {i < 6 && <div className="w-4 h-0.5 ms-1 -me-3 bg-gray-500"></div>}
              </div>
            ))}
          </div>
        ) : (hasData && !isMicroEmpty) ? (
          <div className="mt-4 flex flex-col md:flex-row justify-center items-center overflow-y-auto md:overflow-x-auto progress-section pt-7 pb-4 gap-4 md:gap-0">
            {Object.entries(microSkillsMap).map(([code, micro], idx) => {            
              const microIdx = microCount - 1;
              const isFirstHalf = idx < Math.floor(microIdx / 2);
              const isLast = idx === microIdx;
              const title = micro.title;
              const description = micro.description;
              const status = micro.learning_status;
              const completed = status === 'completed';
              const locked = status === 'locked';
              const ongoing = ongoingIdx === idx;
              const grayscale = completed || locked;
              const ucode = micro.unique_code || micro.code;
              const microTitle = title || description || 'Unnamed Microskill';
              const microCode = ucode || '';
                return (
                  <div key={idx} className="flex flex-col md:flex-row items-center" onClick={() => goToSkillstree(cId, sId, micro.id || micro.micro_skill_id)}>                
                    {ongoing && <div className="absolute animate-bounce -mt-32 ms-[2.6rem] md:block hidden">
                      <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.13785 11.4167C7.54339 12.4464 6.05724 12.4464 5.46278 11.4167L0.208742 2.31647C-0.385718 1.28683 0.357358 -0.000214166 1.54628 -0.00021427L12.0543 -0.000215189C13.2433 -0.000215293 13.9863 1.28683 13.3919 2.31646L8.13785 11.4167Z" fill="#EE6824"/>
                      </svg>
                    </div>}
                    <div 
                      className={`cursor-pointer skillstree-circle ${grayscale ? 'skillstree-circle-inactive' : 'skillstree-circle-active'} ${isLast ? 'skillstree-circle-blue' : isFirstHalf ? 'skillstree-circle-orange' : 'skillstree-circle-green'}`}
                      title={`${ucode}: ${description}`}
                    >
                      {grayscale && <div className="absolute inset-0 bg-black/30 backdrop-grayscale"></div>}
                      <div className="skillstree-circle-content">
                        <div className="skillstree-circle-text">
                          <Check size={42} className={`absolute z-30 text-white/80 ${completed ? 'block' : 'hidden'}`} />
                          <Lock size={38} className={`absolute z-30 text-white/80 ${locked ? 'block' : 'hidden'}`} />
                          {microCode && <div className="skillstree-circle-code">{microCode}</div>}
                          <div className="skillstree-circle-description">{microTitle}</div>
                        </div>
                      </div>
                    </div>
                    {/* Connecting line */}
                    {!isLast && <div className="skillstree-circle-connector my-2 md:my-0 md:mx-1" />}
                  </div>
                )}
            )}
          </div>) : (
          <div className="mt-4 flex justify-center overflow-x-auto space-x-4 pt-7 pb-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="flex items-center">
                <div className="w-24 h-24 border-2 border-gray-400 rounded-full flex-shrink-0 flex items-center justify-center text-xs text-wrap"></div>
                {i < 6 && <div className="w-4 h-0.5 ms-1 -me-3 bg-gray-400"></div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};